import React, { useCallback, useEffect, useRef, useState } from "react";
import { IGeoLocation, IGeoLocationList } from "./core/_models";
import { decodeBase64, getProjectColor, getProjectStageLink } from "../../../utils/config";
import ProjectGoogleMap from "../apps/components/ProjectGoogleMap";
import { getProjectGeoLocations } from "./core/_request";
import { useSnackbar } from "../../../_theme/context/SnackbarContext";
import SimpleLoader from "../apps/components/SimpleLoader";
import { ApiResponse, IDashboardFilterData } from "../core/_models";
import { useAuth } from "../auth";
import { getApiErrorMessage, hasGeoCoordinates } from "../../../utils/utils";
import { isAbortedError } from "../../../utils/global";
import { Link, useLocation } from "react-router-dom";

type Props = {
  filters: IDashboardFilterData;
  search: string;
  label: string;
  dashboard: string;
};

interface IProjectLocationData extends IGeoLocation {
  project_id: string;
  project_name: string;
  budget: string | number;
  sector_name?: string;
}
interface IProjectLocation extends ApiResponse {
  data: IProjectLocationData[];
}

const createMarkerIcon = (color: string) =>
  `data:image/svg+xml;utf-8,
      <svg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 24 24' fill='${color}'>
        <path d='M12 2C8.1 2 5 5.1 5 9c0 5.2 7 13 7 13s7-7.8 7-13c0-3.9-3.1-7-7-7zm0 9.5c-1.4 0-2.5-1.1-2.5-2.5S10.6 6.5 12 6.5s2.5 1.1 2.5 2.5S13.4 11.5 12 11.5z'/>
      </svg>`;

const mapGeoLocations = (data: IProjectLocationData[]): IGeoLocationList[] =>
  data.filter((loc) => loc.loc_id != null && hasGeoCoordinates(loc.geo_lat, loc.geo_long)).map((loc) => ({
    loc_id: loc.loc_id,
    geo_location: loc.geo_location,
    geo_lat: parseFloat(loc.geo_lat),
    geo_long: parseFloat(loc.geo_long),
    erf_number: loc.erf_number,
    project_name: loc.project_name,
    project_index: parseInt(loc.project_id),
    budget: loc.budget,
    sector_name: loc.sector_name,
  }));

const mapUniqueProjects = (
  data: IProjectLocationData[]
): { project_id: number; project_name: string }[] =>
  Array.from(new Map(data.map((loc) => [loc.project_id, loc.project_name]))).map(
    ([project_id, project_name]) => ({
      project_id: parseInt(project_id),
      project_name,
    })
  );

const ProjectMap: React.FC<Props> = ({ filters, search, label, dashboard }) => {
  const { showSnackbar } = useSnackbar();
  const data = Object.fromEntries(new URLSearchParams(window.location.search));
  const location = useLocation();
  const { auth } = useAuth();
  const [activeProjectIndex, setActiveProjectIndex] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [allGeoLocations, setAllGeoLocations] = useState<IGeoLocationList[]>([]);
  const [projects, setProjects] = useState<{ project_id: number, project_name: string }[]>([]);
  const abortRef = useRef<AbortController | null>(null);
  const filtersKey = JSON.stringify(filters);

  useEffect(() => {
    if (!auth?.user_id) {
      return;
    }

    const timeoutId = window.setTimeout(async () => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setLoading(true);

      try {
        const dashboardType = decodeBase64(dashboard)
          .toLowerCase()
          .trim()
          .replace(/\s+/g, "_");
        const parsedFilters = JSON.parse(filtersKey) as IDashboardFilterData;
        const projectResponse = (await getProjectGeoLocations(
          auth.user_id,
          parsedFilters,
          dashboardType,
          search,
          label,
          controller.signal
        )) as IProjectLocation;

        if (controller.signal.aborted) return;

        if (projectResponse.status !== "ok") {
          showSnackbar(
            projectResponse.message || "Failed to fetch project locations",
            "danger"
          );
          return;
        }

        setAllGeoLocations(mapGeoLocations(projectResponse.data));
        setProjects(mapUniqueProjects(projectResponse.data));
      } catch (error) {
        if (isAbortedError(error)) return;
        console.error("Failed to fetch projects locations:", error);
        showSnackbar(getApiErrorMessage(error, "Failed to fetch project locations"), "danger");
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }, 1000);

    return () => {
      abortRef.current?.abort();
      window.clearTimeout(timeoutId);
    };
  }, [auth?.user_id, dashboard, filtersKey, search, label, showSnackbar]);


  const handleProjectClick = useCallback((index: number) => {
    const locations = allGeoLocations.filter((loc) => loc.project_index === index);
    if (locations.length > 0) {
      setActiveProjectIndex(index);
    } else {
      setActiveProjectIndex(null);
      showSnackbar("No locations found for this project", "danger");
    }
  }, [allGeoLocations]);
  return (
    <div className="row g-3">
      {loading && <SimpleLoader />}
      <div className="col-12 col-md-8">
        <ProjectGoogleMap
          allGeoLocations={allGeoLocations}
          activeProjectIndex={activeProjectIndex}
        />
      </div>
      <div className="col-12 col-md-4">
        <div className="overflow-y-auto" style={{ maxHeight: 500 }}>
        <table
          className="table table-bordered border-secondary align-middle w-100 mb-0"
          style={{ borderCollapse: "separate", borderSpacing: 0 }}
        >
          <thead>
            <tr>
              <th className="bg-white position-sticky top-0" style={{ zIndex: 1 }}>Marker</th>
              <th className="bg-white position-sticky top-0" style={{ zIndex: 1 }}>
                <div className="d-flex justify-content-between align-items-center">
                  <span>Project</span>
                  <button className="btn btn-sm btn-none p-0 text-gray-900 fw-bold" onClick={() => setActiveProjectIndex(null)}>Clear</button>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {projects.map((project) => {
              const color = getProjectColor(project.project_id);
              return (
                <tr key={project.project_id}>
                  <td>
                    <a href="#" onClick={() => handleProjectClick(project.project_id)}><img src={createMarkerIcon(color)} /></a>
                  </td>
                  <td>
                  <Link state={{ from: location.state?.from || location.pathname, }} to={`${getProjectStageLink(project.project_id)}/?label=${data.label}&dashboard=${data.dashboard}&url=${data.url}&filter=${data.filter}`} className="text-decoration-none text-dark"> {project.project_name}</Link></td>
                </tr>
              )
            })}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
};

export { ProjectMap };